"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import {
  getVehicleBrands,
  getModelsByBrand,
  getModelYears,
  getVehicleModificationGroups,
} from "@/services/vehicleService";
import { useVehicleSelection } from "@/context/VehicleContext";
import { logger } from "@/utils/logger";
import { normalizeHeroSlides } from "./hero/heroSlides";
import { Sparkles } from "lucide-react";
import Image from "next/image";

const HeroDisplay = dynamic(
  () => import("./hero/HeroDisplay").then((mod) => mod.HeroDisplay),
);
const HeroFinder = dynamic(
  () => import("./hero/HeroFinder").then((mod) => mod.HeroFinder),
);

const toId = (value) => String(value || "");

const getItemId = (item) =>
  item?._id ||
  item?.id ||
  item?.vehicleId ||
  item?.year ||
  item?.code ||
  item?.slug ||
  "";

const HERO_BACKDROP_SRC =
  "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=70&w=1600&auto=format&fit=crop";

export const FigmaHeroSection = ({
  slides = [],
  showDisplayBanner = true,
  showOverlay = true,
  displayLabel = "Featured Display",
}) => {
  const router = useRouter();
  const { selection, setSelection, clearSelection } = useVehicleSelection();
  const fetchSeqRef = useRef(0);
  const brandsLoadedRef = useRef(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const heroSlides = useMemo(() => normalizeHeroSlides(slides), [slides]);

  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandId, setBrandId] = useState("");

  const loadBrands = useCallback(async () => {
    if (brandsLoadedRef.current || brandsLoading) return;

    setBrandsLoading(true);
    try {
      const data = await getVehicleBrands();
      const normalized = Array.isArray(data) ? data : [];
      setBrands(normalized);
      brandsLoadedRef.current = true;
    } catch (error) {
      logger.error("[FIGMA_HOME] Failed to fetch vehicle brands:", error);
    } finally {
      setBrandsLoading(false);
    }
  }, [brandsLoading]);
  const [modelId, setModelId] = useState("");
  const [yearId, setYearId] = useState("");
  const [variantId, setVariantId] = useState("");
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [modificationGroups, setModificationGroups] = useState([]);
  const [modificationOptions, setModificationOptions] = useState([]);
  const [loadingState, setLoadingState] = useState({
    models: false,
    years: false,
    variants: false,
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const media = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(media.matches);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!brandId) return;

    let cancelled = false;
    const loadModels = async () => {
      setLoadingState((prev) => ({ ...prev, models: true }));
      try {
        const data = await getModelsByBrand(brandId);
        if (!cancelled) {
          setModels(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        logger.error("[FIGMA_HOME] Failed to fetch models:", error);
      } finally {
        if (!cancelled) {
          setLoadingState((prev) => ({ ...prev, models: false }));
        }
      }
    };

    loadModels();
    return () => {
      cancelled = true;
    };
  }, [brandId]);

  useEffect(() => {
    if (!brandId || !modelId) return;

    let cancelled = false;
    const loadYears = async () => {
      setLoadingState((prev) => ({ ...prev, years: true }));
      try {
        const data = await getModelYears(modelId, brandId);
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => Number(b.year || 0) - Number(a.year || 0))
          : [];
        if (!cancelled) {
          setYears(sorted);
        }
      } catch (error) {
        logger.error("[FIGMA_HOME] Failed to fetch years:", error);
      } finally {
        if (!cancelled) {
          setLoadingState((prev) => ({ ...prev, years: false }));
        }
      }
    };

    loadYears();
    return () => {
      cancelled = true;
    };
  }, [brandId, modelId]);

  const resolveYearId = useCallback((value) => {
    if (!value) return "";
    const match =
      years.find((item) => toId(getItemId(item)) === toId(value)) ||
      years.find((item) => String(item?.year || "") === String(value));
    return match ? String(getItemId(match) || value) : String(value);
  }, [years]);

  useEffect(() => {
    if (!brandId || !modelId || !yearId) return;

    let cancelled = false;
    const loadVariants = async () => {
      const seq = ++fetchSeqRef.current;
      setLoadingState((prev) => ({ ...prev, variants: true }));
      try {
        const groups = await getVehicleModificationGroups({
          brandId,
          modelId,
          yearId,
        });
        const normalizedGroups = Array.isArray(groups) ? groups : [];
        const options = normalizedGroups.flatMap((group) =>
          Array.isArray(group.options) ? group.options : [],
        );
        if (!cancelled && seq === fetchSeqRef.current) {
          setModificationGroups(normalizedGroups);
          setModificationOptions(options);
        }
      } catch (error) {
        logger.error("[FIGMA_HOME] Failed to fetch variants:", error);
      } finally {
        if (!cancelled && seq === fetchSeqRef.current) {
          setLoadingState((prev) => ({ ...prev, variants: false }));
        }
      }
    };

    loadVariants();
    return () => {
      cancelled = true;
    };
  }, [brandId, modelId, yearId]);

  const handleBrandChange = useCallback((value) => {
    fetchSeqRef.current += 1;
    setBrandId(value);
    setModelId("");
    setYearId("");
    setVariantId("");
    setModels([]);
    setYears([]);
    setModificationGroups([]);
    setModificationOptions([]);
  }, []);

  const handleModelChange = useCallback((value) => {
    fetchSeqRef.current += 1;
    setModelId(value);
    setYearId("");
    setVariantId("");
    setYears([]);
    setModificationGroups([]);
    setModificationOptions([]);
  }, []);

  const handleYearChange = useCallback((value) => {
    fetchSeqRef.current += 1;
    const resolvedYearId = resolveYearId(value);
    setYearId(resolvedYearId);
    setVariantId("");
    setModificationGroups([]);
    setModificationOptions([]);
  }, [resolveYearId]);

  const handleSearch = useCallback(() => {
    if (!brandId || !modelId || !yearId || !variantId) return;

    const brand = brands.find(
      (item) => toId(item._id || item.id) === toId(brandId),
    );
    const model = models.find(
      (item) => toId(getItemId(item)) === toId(modelId),
    );
    const year = years.find((item) => toId(getItemId(item)) === toId(yearId));
    const vehicle = modificationOptions.find(
      (item) => toId(item.vehicleId || item._id || item.id) === toId(variantId),
    );
    setSelection({
      brandId,
      brandName: brand?.name || "",
      modelId,
      modelName: model?.name || "",
      yearId,
      yearLabel: year?.year ? String(year.year) : "",
      vehicleIds: [variantId],
      vehicleLabels: vehicle
        ? [vehicle.label || buildVariantLabel(vehicle)]
        : [],
    });

    router.push("/categories");
  }, [
    brandId,
    brands,
    modelId,
    models,
    yearId,
    years,
    variantId,
    modificationOptions,
    setSelection,
    router,
  ]);

  const modelPlaceholder = loadingState.models
    ? "Loading Models..."
    : brandId && !models.length
      ? "No models available"
      : "Select Model";
  const yearPlaceholder = loadingState.years
    ? "Loading Years..."
    : modelId && !years.length
      ? "No years available"
      : "Select Year";
  const variantPlaceholder = loadingState.variants
    ? "Loading Variants..."
    : yearId && !modificationOptions.length
      ? "No variants available"
      : "Select Variant";

  const isSearchEnabled = Boolean(brandId && modelId && yearId && variantId);
  useEffect(() => {
    if (!isDesktop || heroSlides.length <= 1) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [heroSlides.length, isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;
    setActiveSlide((current) =>
      current >= heroSlides.length ? 0 : current,
    );
  }, [heroSlides.length, isDesktop]);

  return (
    <section className="relative min-h-125 overflow-hidden bg-[#0a0a0e] text-white">
      {/* Background with dynamic overlay */}
      <div className="absolute inset-0 z-0">
        <Image
          src={HERO_BACKDROP_SRC}
          alt="Car parts backdrop"
          fill
          priority
          fetchPriority="high"
          loading="eager"
          quality={60}
          sizes="(min-width: 1024px) 85vw, 1px"
          className="hidden object-cover opacity-60 saturate-50 lg:block"
        />
        <div className="absolute inset-0 bg-zinc-950/80" />
        <div className="absolute inset-0 grid-backdrop" />
        <div className="absolute inset-0 bg-linear-to-t from-[#0a0a0e] via-transparent to-[#0a0a0e]/60" />
        {/* Racing amber glow accents */}
        <div className="absolute -left-32 top-1/3 h-105 w-105 rounded-full bg-amber-500/10 blur-[130px]" />
        <div className="absolute -right-24 -top-24 h-90 w-90 rounded-full bg-orange-600/10 blur-[120px]" />
      </div>
      {/* Racing stripe along the bottom edge */}
      <div className="racing-stripe absolute inset-x-0 bottom-0 z-10 h-0.75 opacity-80" />

      <div className="relative z-10 mx-auto w-full max-w-[90rem] px-5 py-14 sm:px-8 lg:px-12 lg:py-16 xl:px-16">
        <div className="grid w-full items-center gap-10 lg:grid-cols-[1fr_1.5fr]">
          <div className="flex flex-col justify-center">
            <div className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-amber-400 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" />
              Reliable Spare Parts & Accessories
            </div>

            <h1 className="font-display max-w-xl text-4xl font-bold uppercase leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl">
              Find the <span className="text-gradient-amber">Exact Part</span> for Your Machine
            </h1>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-zinc-400 sm:text-base">
              Professional database for car owners and garages. Get fitment-aware parts with fast dispatch across India.
            </p>
          </div>

          {isDesktop ? (
            <div className="figma-hero-display">
              <HeroDisplay
                slides={heroSlides}
                activeSlide={activeSlide}
                setActiveSlide={setActiveSlide}
                showDisplayBanner={showDisplayBanner}
                showOverlay={showOverlay}
                displayLabel={displayLabel}
              />
            </div>
          ) : null}
        </div>

        <div className="mt-10">
          <HeroFinder
            brands={brands}
            brandId={brandId}
            modelId={modelId}
            yearId={yearId}
            variantId={variantId}
            models={models}
            years={years}
            modificationGroups={modificationGroups}
            modificationOptions={modificationOptions}
            loadingState={loadingState}
            brandsLoading={brandsLoading}
            modelPlaceholder={modelPlaceholder}
            yearPlaceholder={yearPlaceholder}
            variantPlaceholder={variantPlaceholder}
            isSearchEnabled={isSearchEnabled}
            onBrandChange={handleBrandChange}
            onBrandDropdownOpen={loadBrands}
            onModelChange={handleModelChange}
            onYearChange={handleYearChange}
            onVariantChange={setVariantId}
            onSearch={handleSearch}
            selection={selection}
            onContinueShopping={() => router.push("/categories")}
            onClearSelection={clearSelection}
          />
        </div>
      </div>
    </section>
  );
};
